import { useEffect, useState } from "react";

// Default single-line loader (back-compat). Used when no `stages` prop is passed.
export const Loader = ({ label = "Querying neural network", stages }) => {
    // AI status theater — when `stages` is an array of >=2 strings, the loader cycles
    // through them with a fade transition so users see what the AI is doing instead of a
    // generic spinner. Each stage holds for ~3.2s then advances. The last stage sticks
    // (so we don't loop back to "Reading filings…" while the AI is still finishing).
    const useStages = Array.isArray(stages) && stages.length > 1;
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        if (!useStages) return;
        if (idx >= stages.length - 1) return;
        const t = setTimeout(() => setIdx((i) => Math.min(i + 1, stages.length - 1)), 3200);
        return () => clearTimeout(t);
    }, [idx, useStages, stages]);

    const current = useStages ? stages[idx] : label;

    return (
        <div className="flex flex-col items-center justify-center gap-4 py-12" data-testid="loader">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 min-h-[1.5em] text-center px-4 transition-opacity duration-300" key={current} data-testid="loader-label">
                <span className="inline-block animate-fade-in">{current}</span>
            </div>
            <div className="dot-loader"><span /><span /><span /></div>
            <div className="w-64 h-px bg-zinc-800 scan-line" />
            {useStages && (
                <div className="flex items-center gap-1.5 mt-1" data-testid="loader-stage-pips">
                    {stages.map((_, i) => (
                        <span
                            key={i}
                            className={`w-1.5 h-1.5 transition-colors ${
                                i < idx ? "bg-emerald-500" : i === idx ? "bg-amber-500 animate-pulse" : "bg-zinc-800"
                            }`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export const StatusDot = ({ tone = "amber" }) => {
    const map = { amber: "bg-amber-500", green: "bg-emerald-500", red: "bg-red-500", zinc: "bg-zinc-500" };
    return <span className={`inline-block w-1.5 h-1.5 rounded-full ${map[tone]}`} />;
};

// Pre-baked stage narratives for each AI flow. Kept here so all callers share the same vocabulary.
export const LoaderStages = {
    research: [
        "Pulling public filings and registry records…",
        "Mapping product lines and pricing…",
        "Cross-referencing competitors and recent news…",
        "Estimating revenue and growth signals…",
        "Composing the dossier…",
    ],
    synergy: [
        "Reading both firms' product catalogs…",
        "Scoring strategic overlap and gaps…",
        "Modeling 12-month revenue impact…",
        "Projecting integration effort and risks…",
        "Composing the verdict…",
    ],
    stretch: [
        "Scanning for high-potential adjacent firms…",
        "Stress-testing required capabilities…",
        "Estimating effort vs. uplift per partner…",
        "Drafting the change-step roadmap…",
    ],
    stakeholders: [
        "Mapping the org chart…",
        "Identifying decision-makers and gatekeepers…",
        "Building first-contact angles…",
    ],
    partners: [
        "Indexing your library…",
        "Pair-scoring against your firms…",
        "Ranking by fit and revenue upside…",
    ],
    image: [
        "Composing the product brief…",
        "Generating studio-lit visualization…",
        "Polishing the final frame…",
    ],
    emailSequence: [
        "Studying the verdict and key selling points…",
        "Drafting 5-touch outreach sequence…",
        "Tuning tone and CTAs per day…",
    ],
    email: [
        "Reading the synergy context…",
        "Drafting a concise intro email…",
    ],
};
